import React, { useState, useEffect, useCallback } from 'react';
import {
  Database,
  Server,
  HardDrive,
  Globe,
  Zap,
  Trash2,
  Plus,
  Unplug,
  Edit2,
  ChevronDown,
  Lock
} from 'lucide-react';
import './Sections.css';
import awsLogo from '../../assets/logos/aws-logo.svg';
import gcpLogo from '../../images/gcp-logo.png';
import azureLogo from '../../assets/logos/azure-logo.svg';

import { API_BASE_URL } from '../../config';

function ConnectionsSection({ connections, onRefresh, loading, setLoading, showNotification }) {
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingConnectionId, setEditingConnectionId] = useState(null);
  const [testingConnection, setTestingConnection] = useState(null);
  const [testStatuses, setTestStatuses] = useState({});
  const [formData, setFormData] = useState({
    name: '',
    type: 'postgresql',
    host: '',
    port: '',
    database_name: '',
    username: '',
    password: '',
    ssl_enabled: false,
    cloud_provider: 'on-premise'
  });

  const [discoveredDatabases, setDiscoveredDatabases] = useState([]);
  const [discovering, setDiscovering] = useState(false);
  const [showDiscoveryDropdown, setShowDiscoveryDropdown] = useState(false);
  const [discoveryError, setDiscoveryError] = useState(null);

  useEffect(() => {
    // Automatically set default ports based on database type
    const defaultPorts = {
      postgresql: '5432',
      mysql: '3306',
      mssql: '1433',
      oracle: '1521'
    };

    if (defaultPorts[formData.type]) {
      setFormData(prev => ({ ...prev, port: defaultPorts[formData.type] }));
    }
  }, [formData.type]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleDiscoverDatabases = useCallback(async () => {
    const { type, host, port, username, password, ssl_enabled } = formData;

    // Only attempt discovery if we have basic connectivity details
    // We allow discovery even without username/password to try default credentials
    if (!host || !port || host.length < 3) {
      if (discoveredDatabases.length > 0) setDiscoveredDatabases([]);
      return;
    }

    setDiscovering(true);
    setDiscoveryError(null);
    try {
      const response = await fetch(`${API_BASE_URL}/api/connections/discover`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ type, host, port, username, password, ssl_enabled })
      });

      const data = await response.json();
      if (data.success && Array.isArray(data.databases)) {
        setDiscoveredDatabases(data.databases);
        setDiscoveryError(null);
      } else {
        console.warn('Discovery failed:', data.error);
        setDiscoveredDatabases([]);
        setDiscoveryError(data.error);
      }
    } catch (error) {
      console.error('Error during database discovery:', error);
      setDiscoveredDatabases([]);
      setDiscoveryError('Network error or server unreachable');
    } finally {
      setDiscovering(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.host, formData.port, formData.username, formData.password, formData.type, formData.ssl_enabled]);

  useEffect(() => {
    // Debounce discovery to avoid too many requests while typing
    const timer = setTimeout(() => {
      if (showCreateModal) {
        handleDiscoverDatabases();
      }
    }, 1000);

    return () => clearTimeout(timer);
  }, [formData.host, formData.port, formData.username, formData.password, formData.type, formData.ssl_enabled, formData.cloud_provider, showCreateModal, handleDiscoverDatabases]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const isEditing = !!editingConnectionId;

    try {
      const connectionData = {
        ...formData,
        port: parseInt(formData.port, 10)
      };

      const url = isEditing
        ? `${API_BASE_URL}/api/connections/${editingConnectionId}`
        : `${API_BASE_URL}/api/connections`;

      const method = isEditing ? 'PUT' : 'POST';

      console.log(`${isEditing ? 'Updating' : 'Creating'} connection:`, connectionData);

      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(connectionData)
      });

      const data = await response.json();
      console.log('Response:', data);

      if (data.success) {
        showNotification(`Database connection ${isEditing ? 'updated' : 'created'} successfully!`, 'success');
        setShowCreateModal(false);
        setEditingConnectionId(null);
        setFormData({
          name: '',
          type: 'postgresql',
          host: '',
          port: '',
          database_name: '',
          username: '',
          password: '',
          ssl_enabled: false,
          cloud_provider: 'on-premise'
        });
        onRefresh();
      } else {
        showNotification(data.error || `Failed to ${isEditing ? 'update' : 'create'} connection`, 'error');
        console.error('Error details:', data);
      }
    } catch (error) {
      console.error(`Error ${isEditing ? 'updating' : 'creating'} connection:`, error);
      showNotification(`Failed to ${isEditing ? 'update' : 'create'} connection: ${error.message}`, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleAddConnection = () => {
    setFormData({
      name: '',
      type: 'postgresql',
      host: '',
      port: '5432',
      database_name: '',
      username: '',
      password: '',
      ssl_enabled: false,
      cloud_provider: 'on-premise'
    });
    setEditingConnectionId(null);
    setShowCreateModal(true);
  };

  const handleEditConnection = (connection) => {
    setFormData({
      name: connection.name,
      type: connection.type,
      host: connection.host,
      port: connection.port,
      database_name: connection.database_name,
      username: connection.username,
      password: connection.password,
      ssl_enabled: connection.ssl_enabled || false,
      cloud_provider: connection.cloud_provider || 'on-premise'
    });
    setEditingConnectionId(connection.id);
    setShowCreateModal(true);
  };

  const handleTestConnection = async (connectionId) => {
    setTestingConnection(connectionId);
    try {
      const connection = connections.find(c => c.id === connectionId);
      if (!connection) return;

      const response = await fetch(`${API_BASE_URL}/api/connections/test`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          type: connection.type,
          host: connection.host,
          port: parseInt(connection.port, 10),
          database_name: connection.database_name,
          username: connection.username,
          password: connection.password,
          ssl_enabled: connection.ssl_enabled || false,
          use_ssh_tunnel: connection.use_ssh_tunnel || false,
          ssh_host: connection.ssh_host || '',
          ssh_port: connection.ssh_port || 22,
          ssh_username: connection.ssh_username || '',
          ssh_private_key: connection.ssh_private_key || ''
        })
      });

      const data = await response.json();

      if (data.success) {
        showNotification('Connection test successful!', 'success');
        setTestStatuses(prev => ({ ...prev, [connectionId]: 'success' }));
      } else {
        showNotification(`Connection test failed: ${data.error}`, 'error');
        setTestStatuses(prev => ({ ...prev, [connectionId]: 'failed' }));
      }
    } catch (error) {
      console.error('Error testing connection:', error);
      showNotification('Connection test failed', 'error');
      setTestStatuses(prev => ({ ...prev, [connectionId]: 'failed' }));
    } finally {
      setTestingConnection(null);
    }
  };

  const handleDeleteConnection = async (connectionId) => {
    const connection = connections.find(c => c.id === connectionId);
    if (!connection) return;

    if (!window.confirm(`Are you sure you want to delete the connection "${connection.name}"?`)) {
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/connections/${connectionId}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' }
      });
      const data = await response.json();
      if (data.success) {
        showNotification('Connection deleted successfully!');
        onRefresh();
      } else {
        showNotification('Error: ' + data.error, 'error');
      }
    } catch (error) {
      showNotification('Failed to delete connection', 'error');
    } finally {
      setLoading(false);
    }
  };

  const getDatabaseIcon = (type) => {
    switch (type) {
      case 'postgresql': return <Database size={32} />;
      case 'mysql': return <Server size={32} />;
      case 'mssql': return <HardDrive size={32} />;
      case 'oracle': return <Globe size={32} />;
      default: return <Database size={32} />;
    }
  };

  const getDatabaseColor = (type) => {
    const colors = {
      postgresql: '#336791',
      mysql: '#4479A1',
      mssql: '#CC2927',
      oracle: '#F80000'
    };
    return colors[type] || '#64748b';
  };

  const getProviderColor = (provider) => {
    const colors = {
      aws: '#FF9900',
      azure: '#0089D6',
      gcp: '#4285F4',
      'on-premise': '#64748b',
      other: '#9333ea'
    };
    return colors[provider?.toLowerCase()] || '#64748b';
  };

  const ProviderLogo = ({ provider, size = 32, className = "" }) => {
    const p = provider?.toLowerCase();

    if (p === 'aws') {
      return (
        <img
          src={awsLogo}
          alt="AWS"
          width={Math.round(size * 1.6)}
          height={size}
          className={`${className} aws-logo-img aws-logo-theme-fix`}
          style={{ objectFit: 'contain' }}
        />
      );
    }

    if (p === 'gcp') {
      return (
        <img
          src={gcpLogo}
          alt="GCP"
          width={size}
          height={size}
          className={className}
          style={{ objectFit: 'contain' }}
        />
      );
    }

    if (p === 'azure') {
      return (
        <img
          src={azureLogo}
          alt="Azure"
          width={size}
          height={size}
          className={className}
          style={{ objectFit: 'contain' }}
        />
      );
    }

    return null;
  };

  const getProviderName = (provider) => {
    const names = {
      aws: 'AWS',
      gcp: 'GCP',
      azure: 'Azure',
      'on-premise': 'On-Premise',
      other: 'Other'
    };
    return names[provider?.toLowerCase()] || provider || 'On-Premise';
  };

  return (
    <div className="section-container">
      <div className="section-header">
        <h2>Database Connections</h2>
        <button
          className="btn-primary"
          onClick={handleAddConnection}
        >
          <Plus size={18} />
          Add Connection
        </button>
      </div>

      <div className="connections-grid">
        {connections?.map(connection => (
          <div
            key={connection.id}
            className={`connection-card ${(() => {
                let isDraft = false;
                if (connection.options) {
                    try {
                        const opts = typeof connection.options === 'string' ? JSON.parse(connection.options) : connection.options;
                        isDraft = opts && (opts.is_draft === true || opts.isDraft === true);
                    } catch (e) {}
                }
                return isDraft ? 'draft' : (testStatuses[connection.id] === 'success' ? 'test-success' : testStatuses[connection.id] === 'failed' ? 'test-failed' : '');
            })()}`}
          >
            <div className="connection-header">
              <div
                className="connection-icon"
                style={{
                  color: getDatabaseColor(connection.type),
                  background: `${getDatabaseColor(connection.type)}15`
                }}
              >
                {connection.cloud_provider && ['aws', 'gcp', 'azure'].includes(connection.cloud_provider.toLowerCase()) ? (
                  <ProviderLogo provider={connection.cloud_provider} size={38} />
                ) : (
                  getDatabaseIcon(connection.type)
                )}
              </div>
              <div className="connection-info">
                <h3>{connection.name}</h3>
                <span className="connection-type">
                  {connection.type.toUpperCase()}
                </span>
                {(() => {
                    let isDraft = false;
                    if (connection.options) {
                        try {
                            const opts = typeof connection.options === 'string' ? JSON.parse(connection.options) : connection.options;
                            isDraft = opts && (opts.is_draft === true || opts.isDraft === true);
                        } catch (e) {}
                    }
                    return isDraft && (
                        <span className="status-badge-pill draft" style={{ marginLeft: 8, verticalAlign: 'middle', display: 'inline-block' }}>
                            DRAFT
                        </span>
                    );
                })()}
                <span
                  className="provider-badge-small"
                  style={{
                    backgroundColor: `${getProviderColor(connection.cloud_provider)}20`,
                    color: getProviderColor(connection.cloud_provider),
                    borderColor: `${getProviderColor(connection.cloud_provider)}40`
                  }}
                >
                  {getProviderName(connection.cloud_provider)}
                </span>
              </div>
              <div className="connection-actions">
                <button
                  className="btn-test"
                  onClick={() => handleTestConnection(connection.id)}
                  disabled={testingConnection === connection.id}
                >
                  {testingConnection === connection.id ? <Zap size={16} className="spin" /> : <Zap size={16} />}
                  <span style={{ marginLeft: 8 }}>{testingConnection === connection.id ? 'Testing...' : 'Test'}</span>
                </button>
                <button
                  className="btn-edit-small"
                  onClick={() => handleEditConnection(connection)}
                  disabled={loading}
                  title="Edit Connection"
                >
                  <Edit2 size={18} />
                </button>
                <button
                  className="btn-delete-small"
                  onClick={() => handleDeleteConnection(connection.id)}
                  disabled={loading}
                  title="Delete Connection"
                >
                  <Trash2 size={18} />
                </button>
              </div>
            </div>

            <div className="connection-details">
              <div className="detail-row">
                <span className="detail-label">Host:</span>
                <span className="detail-value">{connection.host}:{connection.port}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Database:</span>
                <span className="detail-value">{connection.database_name}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Username:</span>
                <span className="detail-value">{connection.username}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">SSL:</span>
                <span className="detail-value">{connection.ssl_enabled ? 'Enabled' : 'Disabled'}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Created:</span>
                <span className="detail-value">
                  {new Date(connection.created_at).toLocaleDateString()}
                </span>
              </div>
            </div>
          </div>
        ))}

        {(!connections || connections.length === 0) && (
          <div className="empty-state">
            <div className="empty-icon">
              <Unplug size={64} />
            </div>
            <h3>No database connections yet</h3>
            <p>Add your first database connection to get started with migrations</p>
            <button
              className="btn-primary"
              onClick={handleAddConnection}
            >
              <Plus size={18} style={{ marginRight: 8 }} />
              Add Connection
            </button>
          </div>
        )}
      </div>

      {/* Create Connection Modal */}
      {showCreateModal && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>{editingConnectionId ? 'Edit Database Connection' : 'Add Database Connection'}</h3>
              <button
                className="modal-close"
                onClick={() => {
                  setShowCreateModal(false);
                  setEditingConnectionId(null);
                }}
              >
                ×
              </button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                <div className="form-group">
                  <label htmlFor="name">Connection Name</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    placeholder="e.g., Production PostgreSQL"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="type">
                    Database Type
                  </label>
                  <select
                    id="type"
                    name="type"
                    value={formData.type}
                    onChange={handleInputChange}
                    required
                    disabled={!!editingConnectionId}
                  >
                    <option value="postgresql">PostgreSQL</option>
                    <option value="mysql">MySQL</option>
                    <option value="mssql">SQL Server</option>
                    <option value="oracle">Oracle</option>
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="cloud_provider">
                    Cloud Provider
                  </label>
                  <select
                    id="cloud_provider"
                    name="cloud_provider"
                    value={formData.cloud_provider}
                    onChange={handleInputChange}
                    required
                    disabled={!!editingConnectionId}
                  >
                    <option value="on-premise">On-Premise</option>
                    <option value="aws">AWS (Amazon Web Services)</option>
                    <option value="azure">Azure (Microsoft)</option>
                    <option value="gcp">GCP (Google Cloud)</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="host">
                      Host
                    </label>
                    <input
                      type="text"
                      id="host"
                      name="host"
                      value={formData.host}
                      onChange={handleInputChange}
                      required
                      placeholder="e.g., localhost or db.example.com"
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="port">
                      Port
                    </label>
                    <input
                      type="number"
                      id="port"
                      name="port"
                      value={formData.port}
                      onChange={handleInputChange}
                      required
                      placeholder="e.g., 5432"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="database_name">
                    Database Name
                    {discovering && <Zap size={12} className="spin" style={{ marginLeft: 8, color: '#9333ea' }} />}
                  </label>
                  <div className={`discovery-wrapper ${showDiscoveryDropdown && discoveredDatabases.length > 0 ? 'open' : ''}`}>
                    <input
                      type="text"
                      id="database_name"
                      name="database_name"
                      value={formData.database_name}
                      onChange={handleInputChange}
                      onFocus={() => {
                        setShowDiscoveryDropdown(true);
                        // Trigger discovery if user focuses and it hasn't run yet
                        if (!discovering && discoveredDatabases.length === 0 && formData.host) {
                          handleDiscoverDatabases();
                        }
                      }}
                      onBlur={() => {
                        // Delay blur to allow click on option
                        setTimeout(() => setShowDiscoveryDropdown(false), 300);
                      }}
                      required
                      placeholder="e.g., production_db"
                      autoComplete="off"
                    />
                    <ChevronDown className="discovery-chevron" size={18} />

                    {(showDiscoveryDropdown || discovering) && discoveredDatabases.length > 0 && (
                      <div className="discovery-dropdown">
                        {discoveredDatabases.map((db, idx) => (
                          <div
                            key={idx}
                            className={`discovery-option ${formData.database_name === db ? 'active' : ''}`}
                            onMouseDown={() => {
                              setFormData(prev => ({ ...prev, database_name: db }));
                              setShowDiscoveryDropdown(false);
                            }}
                          >
                            <Zap size={14} />
                            {db}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  {discoveredDatabases.length > 0 && !discovering && (
                    <small className="form-hint success">Found {discoveredDatabases.length} databases on server</small>
                  )}
                  {discoveredDatabases.length === 0 && !discovering && formData.host && (
                    <small className="form-hint">
                      {discoveryError
                        ? (discoveryError.includes('Anonymous') ? "Enter credentials to fetch databases." : discoveryError)
                        : (formData.password ? "" : "Enter your database name manually if discovery is restricted.")}
                    </small>
                  )}
                  {discovering && (
                    <small className="form-hint">Scanning server for databases...</small>
                  )}
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="username">Username</label>
                    <input
                      type="text"
                      id="username"
                      name="username"
                      value={formData.username}
                      onChange={handleInputChange}
                      required
                      placeholder="database user"
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="password">Password</label>
                    <input
                      type="password"
                      id="password"
                      name="password"
                      value={formData.password}
                      onChange={handleInputChange}
                      required
                      placeholder="database password"
                    />
                  </div>
                </div>

                <div className="checkbox-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      name="ssl_enabled"
                      checked={formData.ssl_enabled}
                      onChange={handleInputChange}
                    />
                    <span className="checkmark"></span>
                    Enable SSL/TLS
                  </label>
                </div>
                <div className="form-group">
                    <label htmlFor="database_name">
                      Database Name (Optional)
                    </label>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => {
                    setShowCreateModal(false);
                    setEditingConnectionId(null);
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={loading}
                >
                  {loading ? (editingConnectionId ? 'Saving...' : 'Creating...') : (editingConnectionId ? 'Save Changes' : 'Create Connection')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default ConnectionsSection;